import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ShoppingCart, Plus, Trash2, Search, X } from 'lucide-react';
import sprayService from '../../services/spray';
import purchaseOrderService from '../../services/purchaseOrders';
import { usePurchaseOrderAccess } from '../../hooks/usePurchaseOrderAccess';
import orchardService from '../../services/orchard';
import { ChemicalSearch } from '../../components/spray';
import PageHeader from '../../components/PageHeader';
import nurseryService from '../../services/nursery';
import { stockItemLabel } from '../../utils/nurseryRowSlots';
import type { ChemicalWithRegistration } from '../../types/spray';
import type { Supplier } from '../../types/purchaseOrders';
import type { Farm } from '../../types/orchard';
import type { NurseryStockItem } from '../../types/nursery';

// 'nursery': one of the organisation's stock items, received into a lot.
type ItemType = 'chemical' | 'general' | 'nursery';

const GENERAL_UNITS = ['units', 'pairs', 'boxes', 'each', 'rolls', 'L', 'kg', 'm', 'bags'];
const CHEMICAL_UNITS = ['L', 'mL', 'kg', 'g'];

interface LineItemRow {
  id: string;
  item_type: ItemType;
  chemical: ChemicalWithRegistration | null;
  /** A nursery line's stock item. */
  stock_item_id: string;
  item_description: string;
  quantity_ordered: string;
  unit: string;
  unit_cost: string;
  notes: string;
}

function newRow(overrides?: Partial<LineItemRow>): LineItemRow {
  return {
    id: Math.random().toString(36).slice(2),
    item_type: 'chemical',
    chemical: null,
    stock_item_id: '',
    item_description: '',
    quantity_ordered: '',
    unit: 'L',
    unit_cost: '',
    notes: '',
    ...overrides,
  };
}
export default function PurchaseOrderCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // The farm the order is for. It goes into the order number, so it is asked
  // first and cannot be changed afterwards.
  const [farms, setFarms] = useState<Farm[] | null>(null);
  const [farmId, setFarmId] = useState('');
  const { canEditSuppliers } = usePurchaseOrderAccess();
  // The organisation's own suppliers (Accounts → Suppliers), read once.
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierQuery, setSupplierQuery] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [supplierFreeText, setSupplierFreeText] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [orderedDate, setOrderedDate] = useState('');
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [notes, setNotes] = useState('');
  const [paidByCard, setPaidByCard] = useState(false);
  const [lineItems, setLineItems] = useState<LineItemRow[]>(() => {
    const chemId = searchParams.get('chemical_id');
    const qty = searchParams.get('quantity');
    const unit = searchParams.get('unit') || 'L';
    if (chemId && qty) {
      return [newRow({ quantity_ordered: qty, unit, item_type: 'chemical' })];
    }
    return [newRow()];
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // The organisation's stock items, for nursery lines. Empty (and the choice
  // hidden) for anyone without a Nursery or Retail seat.
  const [stockItems, setStockItems] = useState<NurseryStockItem[]>([]);
  // A shop arrives from /retail with ?kind=nursery: its lines start as stock.
  const nurseryFirst = searchParams.get('kind') === 'nursery';

  useEffect(() => {
    let cancelled = false;
    nurseryService.getStockItems({ active: true })
      .then(res => {
        if (cancelled || !res.success) return;
        const list = res.data || [];
        setStockItems(list);
        if (nurseryFirst && list.length) {
          setLineItems(prev => prev.map(r => (
            !r.chemical && !r.item_description && !r.quantity_ordered
              ? { ...r, item_type: 'nursery', unit: 'each' }
              : r
          )));
        }
      })
      .catch(() => { /* no stock items: nursery lines are not offered */ });
    return () => { cancelled = true; };
  }, [nurseryFirst]);

  useEffect(() => {
    const chemId = searchParams.get('chemical_id');
    if (!chemId) return;
    sprayService.searchChemicals('', 1, 1).then(() => {
      sprayService.getChemical(parseInt(chemId)).then(res => {
        if (res.success && res.data) {
          const chem = res.data as unknown as ChemicalWithRegistration;
          setLineItems(prev => prev.map((row, i) =>
            i === 0 ? { ...row, chemical: chem } : row
          ));
        }
      });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    // An advisor raises the order for the organisation they are acting for.
    orchardService.getFarms(1, 200, sprayService.getTargetOrgId() ?? undefined)
      .then(res => {
        if (cancelled) return;
        const list = (res.success && res.data?.farms) || [];
        setFarms(list);
        if (list.length === 1) setFarmId(String(list[0].id));
      })
      .catch(() => { if (!cancelled) setFarms([]); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    purchaseOrderService.listSuppliers()
      .then(res => { if (!cancelled && res.success && res.data) setSuppliers(res.data); })
      .catch(() => { /* the name can still be typed */ });
    return () => { cancelled = true; };
  }, []);

  const query = supplierQuery.trim().toLowerCase();
  const supplierResults = (query ? suppliers.filter(s => s.name.toLowerCase().includes(query)) : suppliers).slice(0, 50);

  const chosenFarm = farms?.find(f => String(f.id) === farmId);

  function updateRow(id: string, patch: Partial<LineItemRow>) {
    setLineItems(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  }

  function addRow() {
    setLineItems(prev => [...prev, nurseryFirst && stockItems.length
      ? newRow({ item_type: 'nursery', unit: 'each' })
      : newRow()]);
  }

  function chooseStockItem(rowId: string, id: string) {
    const item = stockItems.find(s => String(s.id) === id);
    updateRow(rowId, { stock_item_id: id, unit: item?.unit || 'each' });
  }

  function removeRow(id: string) {
    setLineItems(prev => prev.filter(r => r.id !== id));
  }

  function clearSupplier() {
    setSelectedSupplier(null);
    setSupplierQuery('');
    setSupplierFreeText('');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    if (farms && farms.length > 0 && !farmId) {
      setSubmitError('Please choose the farm this order is for.');
      return;
    }

    for (const row of lineItems) {
      if (row.item_type === 'chemical' && !row.chemical) {
        setSubmitError('Please select a chemical for all chemical line items.');
        return;
      }
      if (row.item_type === 'general' && !row.item_description.trim()) {
        setSubmitError('Please enter a description for all general line items.');
        return;
      }
      if (row.item_type === 'nursery' && !row.stock_item_id) {
        setSubmitError('Please choose the stock item for each nursery line.');
        return;
      }
      if (!row.quantity_ordered || parseFloat(row.quantity_ordered) <= 0) {
        setSubmitError('Please enter a valid quantity for all line items.');
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await purchaseOrderService.create({
        farm_id: farmId ? parseInt(farmId) : null,
        supplier_id: selectedSupplier?.id ?? null,
        supplier_name: selectedSupplier ? selectedSupplier.name : (supplierFreeText.trim() || null),
        ordered_date: orderedDate || null,
        expected_delivery_date: expectedDelivery || null,
        notes: notes.trim() || null,
        paid_by_card: paidByCard,
        line_items: lineItems.map(row => ({
          item_type: row.item_type,
          chemical_id: row.item_type === 'chemical' ? row.chemical?.id ?? null : null,
          item_description: row.item_type === 'general' ? row.item_description : null,
          ...(row.item_type === 'nursery' ? { stock_item_id: parseInt(row.stock_item_id, 10) } : {}),
          quantity_ordered: parseFloat(row.quantity_ordered),
          unit: row.unit,
          unit_cost: row.unit_cost ? parseFloat(row.unit_cost) : null,
          notes: row.notes.trim() || null,
        })),
      });

      if (res.success && res.data) {
        navigate(`/purchase-orders/${res.data.id}`);
      } else {
        setSubmitError('Failed to create purchase order. Please try again.');
      }
    } catch {
      setSubmitError('Failed to create purchase order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <div className="min-h-screen bg-gray-50">
      <>
        <PageHeader
          title="New Purchase Order"
          subtitle="Raise a chemical or supply order with a supplier, for a farm"
          icon={<ShoppingCart className="h-6 w-6 text-white" />}
        />
        <div className="max-w-3xl mx-auto px-4 py-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Farm */}
            {farms && farms.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <label htmlFor="po-farm" className="block text-sm font-semibold text-gray-900 mb-1">Farm</label>
                <p className="text-xs text-gray-500 mb-3 break-words">
                  The farm this order is for. Its code becomes part of the order number, so accounts can see
                  what each order was for, and the farm cannot be changed once the order is created.
                </p>
                <select
                  id="po-farm"
                  value={farmId}
                  onChange={e => setFarmId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Choose a farm…</option>
                  {farms.map(f => (
                    <option key={f.id} value={f.id}>{f.code ? `${f.name} (${f.code})` : f.name}</option>
                  ))}
                </select>
                {chosenFarm?.code && (
                  <p className="text-xs text-gray-500 mt-2 break-words">
                    This order&rsquo;s number will start PO-{chosenFarm.code}-. The code is set on the farm&rsquo;s page.
                  </p>
                )}
              </div>
            )}

            {/* Supplier */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-4">Supplier</h2>

              {selectedSupplier ? (
                <div className="flex items-start justify-between gap-2 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-indigo-900 break-words">{selectedSupplier.name}</p>
                    {selectedSupplier.contact_email && (
                      <p className="text-xs text-indigo-600 break-all">{selectedSupplier.contact_email}</p>
                    )}
                    {selectedSupplier.contact_phone && (
                      <p className="text-xs text-indigo-600">{selectedSupplier.contact_phone}</p>
                    )}
                    <p className="text-xs text-indigo-600 mt-1 break-all">
                      {selectedSupplier.po_email || selectedSupplier.contact_email
                        ? `Orders are sent to ${selectedSupplier.po_email || selectedSupplier.contact_email}`
                        : 'No email on file to send the order to.'}
                    </p>
                    {!(selectedSupplier.po_email || selectedSupplier.contact_email) && (
                      <p className="text-xs text-indigo-600 mt-1 break-words">
                        {canEditSuppliers ? (
                          <Link to={`/accounts/suppliers?edit=${selectedSupplier.id}`} className="underline">Add one in Accounts → Suppliers</Link>
                        ) : (
                          'An Accounts seat adds one under Accounts → Suppliers.'
                        )}
                      </p>
                    )}
                  </div>
                  <button type="button" onClick={clearSupplier} className="text-indigo-400 hover:text-indigo-600">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2">
                    <Search className="h-4 w-4 text-gray-400 shrink-0" />
                    <input
                      type="text"
                      value={supplierQuery}
                      onChange={e => { setSupplierQuery(e.target.value); setShowSupplierDropdown(true); }}
                      onFocus={() => setShowSupplierDropdown(true)}
                      onBlur={() => setTimeout(() => setShowSupplierDropdown(false), 200)}
                      placeholder={suppliers.length ? 'Search your suppliers…' : 'No suppliers on the list yet'}
                      className="flex-1 text-sm focus:outline-none bg-transparent"
                    />
                  </div>
                  {showSupplierDropdown && supplierResults.length > 0 && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {supplierResults.map(s => (
                        <button
                          key={s.id}
                          type="button"
                          onMouseDown={() => { setSelectedSupplier(s); setSupplierQuery(s.name); setShowSupplierDropdown(false); }}
                          className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                        >
                          <span className="font-medium text-gray-900">{s.name}</span>
                          {s.contact_email && <span className="text-gray-400 ml-2">{s.contact_email}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2 break-words">
                    Suppliers are kept in Accounts.{' '}
                    {canEditSuppliers
                      ? <Link to="/accounts/suppliers" className="text-indigo-600 underline">Add or change one</Link>
                      : 'Someone with an Accounts seat adds them, with the email orders are sent to.'}
                  </p>
                  <div className="mt-2">
                    <label className="block text-xs text-gray-500 mb-1">
                      Not on the list? Type the name (the order cannot be emailed until the supplier is added):
                    </label>
                    <input
                      type="text"
                      value={supplierFreeText}
                      onChange={e => setSupplierFreeText(e.target.value)}
                      placeholder="Free-text supplier name"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Dates & Notes */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-4">Order Details</h2>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Order Date</label>
                  <input
                    type="date"
                    value={orderedDate}
                    onChange={e => setOrderedDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Expected Delivery</label>
                  <input
                    type="date"
                    value={expectedDelivery}
                    onChange={e => setExpectedDelivery(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Additional notes..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>
              <label className="flex items-start gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  checked={paidByCard}
                  onChange={e => setPaidByCard(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0"
                />
                <span className="min-w-0 break-words">
                  <span className="font-medium">Paid on the company card</span>
                  <span className="block text-xs text-gray-500">
                    For something already paid for, such as at the counter. Accounts gets it as a card payment, not
                    a bill to wait for. Enter each line&rsquo;s cost and attach the receipt once it is raised.
                  </span>
                </span>
              </label>
            </div>

            {/* Line Items */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-gray-900">Line Items</h2>
                <button
                  type="button"
                  onClick={addRow}
                  className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Item
                </button>
              </div>

              <div className="space-y-4">
                {lineItems.map((row, idx) => (
                  <div key={row.id} className="border border-gray-200 rounded-lg p-4 relative">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-medium text-gray-500">Item {idx + 1}</span>
                      <div className="flex items-center gap-3">
                        {/* Type toggle */}
                        <div className="flex rounded-lg overflow-hidden border border-gray-300 text-xs">
                          <button
                            type="button"
                            onClick={() => updateRow(row.id, { item_type: 'chemical', chemical: null, unit: 'L' })}
                            className={`px-3 py-1 ${row.item_type === 'chemical' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                          >
                            Chemical
                          </button>
                          <button
                            type="button"
                            onClick={() => updateRow(row.id, { item_type: 'general', chemical: null, unit: 'units' })}
                            className={`px-3 py-1 ${row.item_type === 'general' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                          >
                            General
                          </button>
                          {stockItems.length > 0 && (
                            <button
                              type="button"
                              onClick={() => updateRow(row.id, { item_type: 'nursery', chemical: null, unit: 'each' })}
                              className={`px-3 py-1 ${row.item_type === 'nursery' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                            >
                              Stock
                            </button>
                          )}
                        </div>
                        {lineItems.length > 1 && (
                          <button type="button" onClick={() => removeRow(row.id)} className="text-gray-300 hover:text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {row.item_type === 'nursery' ? (
                      <div className="mb-3">
                        <label htmlFor={`po-stock-${row.id}`} className="sr-only">Stock item</label>
                        <select
                          id={`po-stock-${row.id}`}
                          value={row.stock_item_id}
                          onChange={e => chooseStockItem(row.id, e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">Choose a plant or product…</option>
                          {stockItems.map(s => <option key={s.id} value={s.id}>{stockItemLabel(s)}</option>)}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Received into stock on hand at what it cost, in the place you choose.</p>
                      </div>
                    ) : row.item_type === 'chemical' ? (
                      <div className="mb-3">
                        <ChemicalSearch
                          value={row.chemical}
                          onChange={chem => updateRow(row.id, { chemical: chem, unit: 'L' })}
                        />
                      </div>
                    ) : (
                      <div className="mb-3">
                        <input
                          type="text"
                          value={row.item_description}
                          onChange={e => updateRow(row.id, { item_description: e.target.value })}
                          placeholder="Item description (e.g. Nitrile gloves, Safety goggles...)"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Quantity</label>
                        <input
                          type="number"
                          min="0.001"
                          step="any"
                          value={row.quantity_ordered}
                          onChange={e => updateRow(row.id, { quantity_ordered: e.target.value })}
                          placeholder="0"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Unit</label>
                        {row.item_type === 'nursery' ? (
                          <p className="px-3 py-2 text-sm text-gray-700 break-words">{row.unit}</p>
                        ) : row.item_type === 'chemical' ? (
                          <select
                            value={row.unit}
                            onChange={e => updateRow(row.id, { unit: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            {CHEMICAL_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        ) : (
                          <select
                            value={row.unit}
                            onChange={e => updateRow(row.id, { unit: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            {GENERAL_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Unit Cost ($)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.0001"
                          value={row.unit_cost}
                          onChange={e => updateRow(row.id, { unit_cost: e.target.value })}
                          placeholder="0.00"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>

                    <div className="mt-3">
                      <input
                        type="text"
                        value={row.notes}
                        onChange={e => updateRow(row.id, { notes: e.target.value })}
                        placeholder="Line notes (optional)"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400 text-gray-600"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addRow}
                className="mt-4 w-full flex items-center justify-center gap-2 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-400 hover:border-indigo-400 hover:text-indigo-500 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add Another Item
              </button>
            </div>

            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
                {submitError}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || lineItems.length === 0}
                className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Creating...' : 'Create Purchase Order'}
              </button>
            </div>
          </form>
        </div>
      </>
    </div>
  );
}
